import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
    ArrowLeft, 
    MapPin, 
    Briefcase, 
    DollarSign, 
    Send,
    FileText,
    CheckCircle2,
    Building2
} from 'lucide-react';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import { useGetJobByIdQuery, useApplyForJobMutation } from '../store/slices/apiSlice';
import { usePageMetadata } from '../hooks/usePageMetadata';

const JobDetails = () => {
    const { id } = useParams();
    const { data: jobData, isLoading, error } = useGetJobByIdQuery(Number(id));
    const [applyForJob, { isLoading: isApplying }] = useApplyForJobMutation();
    
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        coverLetter: ''
    });
    const [resumeFile, setResumeFile] = useState<File | null>(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState('');

    usePageMetadata('job-details');

    const job = jobData?.data;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setResumeFile(e.target.files[0]);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!resumeFile) {
            setSubmitError('Please upload your resume.');
            return;
        }

        const data = new FormData();
        data.append('jobId', String(id));
        data.append('fullName', formData.fullName);
        data.append('email', formData.email);
        data.append('phone', formData.phone);
        data.append('coverLetter', formData.coverLetter);
        data.append('file', resumeFile);

        try {
            await applyForJob(data).unwrap();
            setIsSubmitted(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err: any) {
            setSubmitError(err.data?.message || 'Failed to submit application. Please try again.');
        }
    };

    if (isLoading) return (
        <div className="min-h-screen flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
    );

    if (error || !job) return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6">
            <h2 className="text-2xl font-bold mb-4">Job Not Found</h2>
            <Link to="/careers" className="text-blue-600 hover:underline flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" /> Back to Careers
            </Link>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            
            <div className="container mx-auto max-w-5xl px-6 py-12">
                <Link to="/careers" className="inline-flex items-center text-gray-500 hover:text-blue-600 mb-8 transition-colors">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to all jobs
                </Link>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                    {/* Job Details Column */}
                    <div className="lg:col-span-2 space-y-12">
                        <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm">
                            <h1 className="text-4xl font-bold text-gray-900 mb-6">{job.title}</h1>
                            
                            <div className="flex flex-wrap gap-6 mb-8 text-gray-600">
                                <div className="flex items-center gap-2">
                                    <MapPin className="w-5 h-5 text-blue-500" />
                                    {job.location}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Briefcase className="w-5 h-5 text-blue-500" />
                                    {job.type}
                                </div>
                                {job.salary && (
                                    <div className="flex items-center gap-2">
                                        <DollarSign className="w-5 h-5 text-blue-500" />
                                        {job.salary}
                                    </div>
                                )}
                                {job.department && (
                                    <div className="flex items-center gap-2">
                                        <Building2 className="w-5 h-5 text-blue-500" />
                                        {job.department}
                                    </div>
                                )}
                            </div>

                            <div className="prose prose-blue max-w-none text-gray-600 leading-relaxed space-y-6">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-3">About the Role</h3>
                                    <p>{job.fullDescription}</p>
                                </div>

                                {job.responsibilities && (
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-3">Responsibilities</h3>
                                        <ul className="list-disc pl-5 space-y-2">
                                            {job.responsibilities.split('\n').map((item, idx) => (
                                                <li key={idx}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {job.requirements && (
                                    <div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-3">Requirements</h3>
                                        <ul className="list-disc pl-5 space-y-2">
                                            {job.requirements.split('\n').map((item, idx) => (
                                                <li key={idx}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Application Form Column */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-24">
                            {isSubmitted ? (
                                <div className="bg-green-50 rounded-3xl p-10 border border-green-100 text-center animate-in fade-in zoom-in duration-500">
                                    <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                                        <CheckCircle2 className="w-10 h-10 text-green-600" />
                                    </div>
                                    <h2 className="text-2xl font-bold text-green-900 mb-2">Application Sent!</h2>
                                    <p className="text-green-700 mb-6">
                                        Thank you for your interest. Our team will review your application and get back to you soon.
                                    </p>
                                    <Link to="/careers" className="inline-block px-6 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors">
                                        View more jobs
                                    </Link>
                                </div>
                            ) : (
                                <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-xl">
                                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                                        Apply Now
                                    </h2>
                                    
                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                                            <input 
                                                type="text" 
                                                name="fullName"
                                                required
                                                value={formData.fullName}
                                                onChange={handleInputChange}
                                                placeholder="John Doe"
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                                            <input 
                                                type="email" 
                                                name="email"
                                                required
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                placeholder="john@example.com"
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                                            <input 
                                                type="tel" 
                                                name="phone"
                                                required
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                placeholder="+234 ..."
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Resume (PDF/Doc)</label>
                                            <div className="relative group">
                                                <input 
                                                    type="file" 
                                                    accept=".pdf,.doc,.docx"
                                                    onChange={handleFileChange}
                                                    required
                                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                                />
                                                <div className="w-full px-4 py-3 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl group-hover:border-blue-300 transition-colors flex items-center justify-center gap-2 text-sm text-gray-500">
                                                    <FileText className="w-4 h-4" />
                                                    {resumeFile ? resumeFile.name : 'Upload Resume'}
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Cover Letter (Optional)</label>
                                            <textarea 
                                                name="coverLetter"
                                                rows={4}
                                                value={formData.coverLetter}
                                                onChange={handleInputChange}
                                                placeholder="Why are you a good fit?"
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm resize-none"
                                            ></textarea>
                                        </div>

                                        {submitError && <p className="text-red-500 text-xs mt-2">{submitError}</p>}

                                        <button 
                                            type="submit" 
                                            disabled={isApplying}
                                            className="w-full py-4 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-200 disabled:opacity-50"
                                        >
                                            {isApplying ? (
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                            ) : (
                                                <>
                                                    Submit Application
                                                    <Send className="w-4 h-4" />
                                                </>
                                            )}
                                        </button>
                                        
                                        <p className="text-[10px] text-gray-400 text-center mt-4">
                                            By applying, you agree to our Terms of Service and Privacy Policy.
                                        </p>
                                    </form>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default JobDetails;
